import React from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { useLocation, useNavigate, Location, NavigateFunction } from "react-router-dom";
import { IService, deleteService, getServices } from "../../../redux/modules/services";
import { RootState } from "../../../redux";
import TypeaheadSearch, { SearchType } from "../../TypeaheadSearch";
import { formatPrice } from "../../../utils/price-utils";
import "./service-list.css";

interface ServiceListProps {
  services?: IService[];
  count?: number;
  deleteService?: (serviceId: number) => void;
  getServices?: (page: number, includeArchived?: boolean) => Promise<void>;
  location?: Location;
  navigate?: NavigateFunction;
}

type ServiceListState = {
  page: number;
};

class ServiceListPage extends React.Component<ServiceListProps, ServiceListState> {
  constructor(props: ServiceListProps) {
    super(props);
    this.state = { page: this.getPageFromUrl() };
  }

  getPageFromUrl = (): number => {
    const params = new URLSearchParams(this.props.location?.search || "");
    const p = parseInt(params.get("page") || "1", 10);
    return Math.max(0, isNaN(p) ? 0 : p - 1);
  };

  getArchivedFromUrl = (): boolean => {
    const params = new URLSearchParams(this.props.location?.search || "");
    return params.get("archived") === "true";
  };

  buildUrl = (page: number, archived: boolean): string => {
    const params = new URLSearchParams();
    params.set("page", String(page + 1));
    if (archived) params.set("archived", "true");
    return `?${params.toString()}`;
  };

  componentDidMount() {
    const page = this.getPageFromUrl();
    const archived = this.getArchivedFromUrl();
    this.props.getServices && this.props.getServices(page, archived);
  }

  componentDidUpdate(prevProps: ServiceListProps) {
    if (prevProps.location?.search !== this.props.location?.search) {
      const page = this.getPageFromUrl();
      const archived = this.getArchivedFromUrl();
      this.setState({ page });
      this.props.getServices && this.props.getServices(page, archived);
    }
  }

  renderServices() {
    if (!this.props.services) {
      return (
        <div className="service-list__empty">
          <p className="service-list__loading">Триває пошук...</p>
        </div>
      );
    }

    const sorted = [...this.props.services].sort((c1, c2) => (c1.name < c2.name ? -1 : 1));

    if (sorted.length === 0) {
      return (
        <div className="service-list__empty">
          <p className="service-list__empty-text">Послуги не знайдені</p>
        </div>
      );
    }

    return sorted.map((service) => (
      <div key={service.id} className={`service-list__card ${service.archived ? "service-list__card--archived" : ""}`}>
        <div className="service-list__card-content">
          <Link to={`/service/${service.id}`} className="service-list__card-link">
            <div className="service-list__card-icon">✨</div>
            <div className="service-list__card-info">
              <h3 className="service-list__card-name">{service.name}</h3>
              <div className="service-list__card-details">
                <span className="service-list__card-price">{formatPrice(service.price)} грн</span>
                {service.notes && (
                  <p className="service-list__card-notes">
                    <span className="service-list__card-label">Нотатки:</span> {service.notes}
                  </p>
                )}
              </div>
            </div>
          </Link>
          <div className="service-list__card-actions">{this.renderButtons(service)}</div>
        </div>
      </div>
    ));
  }

  renderButtons(service: IService) {
    if (!service.archived) {
      return (
        <button type="button" className="service-list__btn-archive" onClick={() => this.archiveService(service)} title="Архівувати послугу">
          <span className="service-list__btn-icon">🗄️</span>
        </button>
      );
    }
    return <span className="service-list__badge-archived">Архівована</span>;
  }

  render() {
    const showArchived = this.getArchivedFromUrl();

    return (
      <div className="service-list">
        <div className="service-list__header">
          <h1 className="service-list__title">Послуги</h1>
          <p className="service-list__subtitle">Каталог послуг</p>
        </div>

        <div className="service-list__actions">
          <Link to="/new-service" className="service-list__btn-add">
            <span className="service-list__btn-add-icon">➕</span>
            Додати Послугу
          </Link>
        </div>

        <div className="service-list__search-section">
          <div className="service-list__search-card">
            <h2 className="service-list__search-title">Швидкий пошук</h2>
            <TypeaheadSearch searchType={SearchType.SERVICES} />
          </div>
        </div>

        <div className="service-list__filters">
          <div className="service-list__filter-toggle">
            <button
              className={`service-list__filter-btn${!showArchived ? " service-list__filter-btn--active" : ""}`}
              onClick={() => this.setArchived(false)}
            >
              Тільки активні
            </button>
            <button
              className={`service-list__filter-btn${showArchived ? " service-list__filter-btn--active" : ""}`}
              onClick={() => this.setArchived(true)}
            >
              Тільки архівні
            </button>
          </div>
        </div>

        <div className="service-list__content">
          <div className="service-list__grid">{this.renderServices()}</div>
          {this.renderPagination()}
        </div>
      </div>
    );
  }

  renderPagination = () => {
    const { count } = this.props;
    if (!count) return null;

    const PAGE_SIZE = 20;
    const totalPages = Math.ceil(count / PAGE_SIZE);
    if (totalPages <= 1) return null;

    const current = this.state.page;
    const archived = this.getArchivedFromUrl();
    const pages: (number | "ellipsis")[] = [];

    if (totalPages <= 7) {
      for (let i = 0; i < totalPages; i++) pages.push(i);
    } else {
      const visible = new Set(
        [0, totalPages - 1, current - 2, current - 1, current, current + 1, current + 2].filter((p) => p >= 0 && p < totalPages)
      );
      const sorted = Array.from(visible).sort((a, b) => a - b);
      for (let i = 0; i < sorted.length; i++) {
        if (i > 0 && sorted[i] - sorted[i - 1] > 1) pages.push("ellipsis");
        pages.push(sorted[i]);
      }
    }

    return (
      <div className="service-list__pagination">
        <button
          className="service-list__pagination-btn service-list__pagination-btn--nav"
          disabled={current === 0}
          onClick={() => this.goToPage(current - 1, archived)}
        >
          ←
        </button>
        {pages.map((p, i) =>
          p === "ellipsis" ? (
            <span key={`ellipsis-${i}`} className="service-list__pagination-ellipsis">
              …
            </span>
          ) : (
            <button
              key={p}
              className={`service-list__pagination-btn${p === current ? " service-list__pagination-btn--active" : ""}`}
              onClick={() => this.goToPage(p, archived)}
            >
              {p + 1}
            </button>
          )
        )}
        <button
          className="service-list__pagination-btn service-list__pagination-btn--nav"
          disabled={current === totalPages - 1}
          onClick={() => this.goToPage(current + 1, archived)}
        >
          →
        </button>
      </div>
    );
  };

  goToPage = (page: number, archived: boolean) => {
    this.props.navigate && this.props.navigate(this.buildUrl(page, archived));
  };

  setArchived = (archived: boolean) => {
    this.props.navigate && this.props.navigate(this.buildUrl(0, archived));
  };

  archiveService = (service: IService) => {
    if (window.confirm(`Ви впевнені, що хочете архівувати послугу "${service.name}"?`)) {
      this.props.deleteService && this.props.deleteService(service.id);
    }
  };
}

const mapStateToProps = (state: RootState) => {
  return { services: Object.values(state.servicesState.services), count: state.servicesState.count };
};

const ConnectedServiceListPage = connect(mapStateToProps, { getServices, deleteService })(ServiceListPage);

function ServiceListPageWithRouter(props: object) {
  const location = useLocation();
  const navigate = useNavigate();
  return <ConnectedServiceListPage {...props} location={location} navigate={navigate} />;
}

export default ServiceListPageWithRouter;
